import CardCommon from '@op/components/CardCommon';
import {
  vsIcon,
  barIcon,
  SymbolType,
  lineIcon,
  stackLineIcon,
  tableIcon,
  pieIcon,
  commonBarHIcon,
} from '@op/constants/chart';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ByKeyMetricsType,
  CommunityOptionsType,
  defaultFilterReviewTypes,
  getEngagementSortItem,
  getReviewTypesFromMedia,
  KeyMetricsType,
} from '../constants';
import { useHistory, useIntl } from 'umi';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { NO_DATE } from '@op/constants';
import Table from './Table';
import Chart from './Chart';
import {
  getKeyMetricsCommunityDownloadData,
  useKeyMetricsCommunityChart,
} from '../../hooks';
import { formatDateFunc } from '@op/utils/time';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { useFilterCondConf } from '@op/hooks/useFilterCondConf';
import DetailWrapper from '@op/pages/Detail/component/DetailWrapper';
import { useStableValue } from '@opm/utils/stableKey';
import './index.scss';
import { DBO_V } from '@op/utils/format';
import { JumpType } from '@home/utils/utils';

function getDimensionKey(byType: ByKeyMetricsType): string | null {
  if (byType === 'by_channel') return 'channel';
  if (byType === 'by_language') return 'language';
  if (byType === 'by_media_type') return 'review_types';
  return null;
}

export default function LeftChart({
  tab,
  keyMetric,
  title,
  filter,
  paramsKeyMetric,
  getOtherParams,
  byType,
  setByType,
  options,
  isDetail = false,
  updateDetailFilter,
  detailPage = false,
  defaultChartType,
  gameInfo,
}: {
  tab: CommunityOptionsType;
  keyMetric: { value: KeyMetricsType; format?: (value: number) => any; };
  title?: string;
  filter: any;
  paramsKeyMetric?: string;
  getOtherParams?: (tab: CommunityOptionsType) => any;
  byType: { value: ByKeyMetricsType; };
  options: Array<{ text: string; value: string; }>;
  setByType?: (value: any) => void;
  isDetail?: boolean;
  updateDetailFilter?: (filter: any) => void;
  detailPage?: boolean;
  defaultChartType?: SymbolType;
  gameInfo?: any;
}) {
  const { formatMessage } = useIntl();
  const { download, downloading } = useDownloadParams();
  const formatTimeFunc = formatDateFunc(filter?.date_type);
  const { monitor_by_account_category: monitorByAccountCategory } = useFilterCondConf({
    gameInfo: gameInfo || filter,
    requestList: [{ key: 'monitor_by_account_category', defaultValue: {} }],
  });

  const [chartType, setChartType] = useState<SymbolType>(
    defaultChartType || 'line',
  );
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [localByType, setLocalByType] = useState(byType);
  const otherParams = useStableValue(getOtherParams?.(tab));

  useEffect(() => {
    setLocalByType(byType);
  }, [byType]);

  const params = useMemo(() => {
    return {
      ...filter,
      metrics: [paramsKeyMetric],
      tab_type: tab,
      games: [],
      by_type: localByType.value,
      ...(otherParams || {}),
    };
  }, [filter, tab, paramsKeyMetric, localByType.value, otherParams]);

  const formatValue = useMemo(() => {
    if (keyMetric.value === 'engagement_rate') {
      return (value: number) => DBO_V.ratio_min(value);
    }
    return keyMetric.format;
  }, [keyMetric.value]);

  const { data, loading } = useKeyMetricsCommunityChart({ filter: params });

  const history = useHistory();
  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filter);
    } else {
      setChartType(type || 'bar');
    }
  }
  const { Competitor_Rank_Hide } = useGetSingePageSourceRule({
    keys: ['Competitor_Rank_Hide'],
  });
  function getOperationsList() {
    let list = [
      pieIcon,
      tableIcon,
      lineIcon,
      stackLineIcon,
      commonBarHIcon,
      barIcon,
    ];
    if (keyMetric.value === 'engagement_rate') {
      list = [tableIcon, lineIcon, barIcon];
    }

    if (!Competitor_Rank_Hide) {
      list.unshift(vsIcon);
    }

    return list;
  }

  useEffect(() => {
    const list = getOperationsList();

    const chartTypeList = list.map((item) => item.symbol);

    if (!chartTypeList.includes(chartType)) {
      setChartType('line');
    }
  }, [keyMetric]);

  const handleDownloadOnClick = async () => {
    const data = await getKeyMetricsCommunityDownloadData({
      filter: params,
      setDownloadLoading,
    });
    download({
      key: 'KeyMetricsCommunityLeftChart',
      fileName: `Key Metrics Community ${keyMetric.value}`,
      filter,
      auxiliaryParams: {
        columns: data?.tableColumns || [],
        byType: localByType.value,
      },
      data: data?.tableData || [],
    });
  };

  const enableDetailPage = useMemo(() => {
    return detailPage && localByType.value !== 'by_follower_range';
  }, [detailPage, localByType]);

  const eventProps = useMemo(() => {
    const byTypeValue = localByType.value;
    const dimensionKey = getDimensionKey(byTypeValue);
    const engagementSort =
      byTypeValue === 'by_engagement_type' ? { sort_item: 'seriesName' } : {};
    const lineParams = {
      start_time: 'name',
      end_time: 'name',
      ...engagementSort,
      ...(dimensionKey ? { [dimensionKey]: 'seriesName' } : {}),
    };
    const pieNameParams = {
      start_time: 'start_time',
      end_time: 'end_time',
      ...(byTypeValue === 'by_engagement_type'
        ? { sort_item: 'name' }
        : {}),
      ...(dimensionKey ? { [dimensionKey]: 'name' } : {}),
    };

    const defaultFilter = { ...params, channel_type: 'social' };
    if (keyMetric.value !== 'engagement') {
      defaultFilter.review_types = defaultFilterReviewTypes[tab] || [];
    }

    return {
      key:
        tab === 'streaming'
          ? 'KeyMetricsCommunityStreamingLeftChart'
          : 'KeyMetricsCommunityLeftChart',
      defaultFilter,
      defaultProps: {
        tab,
        keyMetric,
        title,
        paramsKeyMetric,
        byType: localByType,
        options: options.filter((item) => item.value !== 'by_follower_range'),
        filter,
        defaultChartType: chartType,
        detailPage: enableDetailPage,
        gameInfo,
      },
      paramsKey: {
        line: lineParams,
        stack_line: lineParams,
        bar: lineParams,
        pie: pieNameParams,
        'common-bar': pieNameParams,
        table: {
          start_time: 'time',
          end_time: 'time',
        },
        ...(byTypeValue !== 'by_engagement_type'
          ? {
            cellClick: {
              start_time: 'time',
              end_time: 'time',
              ...(dimensionKey ? { [dimensionKey]: 'columnKey' } : {}),
            },
          }
          : {}),
      },
      chartType,
      specialParamsKey: {
        bar: {
          start_time: 'selected',
          end_time: 'selected',
        },
      },
    };
  }, [
    localByType,
    chartType,
    enableDetailPage,
    filter,
    keyMetric,
    options,
    params,
    paramsKeyMetric,
    tab,
    title,
    gameInfo,
  ]);

  const formatParamsKey = useCallback(
    (value: string, key: string) => {
      if (
        (key === 'start_time' || key === 'end_time') &&
        (chartType === 'pie' || chartType === 'common-bar')
      ) {
        return { [key]: params[key] };
      }

      if (key === 'sort_item') {
        const sortItem = getEngagementSortItem(value);
        if (sortItem) {
          return { sort_item: sortItem };
        }
        return value;
      }

      if (key === 'review_types') {
        const reviewTypes = getReviewTypesFromMedia(
          value,
          monitorByAccountCategory,
        );
        if (reviewTypes?.length) return { review_types: reviewTypes };
        return value;
      }

      if (value.toLocaleLowerCase() !== 'others') {
        return value;
      }

      const columns = data?.tableColumns || [];
      const exceptKeys =
        columns
          .filter(
            (item: any) =>
              item.key !== 'others' && item.header !== 'Others',
          )
          .map((item: any) => item.key) || [];

      if (key === 'channel') {
        return {
          channel: [],
          exclude_channels: exceptKeys,
        };
      }

      if (key === 'language') {
        return {
          language: [],
          except_language: exceptKeys,
        };
      }

      return value;
    },
    [chartType, data, monitorByAccountCategory, params],
  );

  return (
    <CardCommon
      contentCard={!isDetail}
      operationInfo={[chartType, changeChartType]}
      operations={getOperationsList()}
      hasDownload
      handleDownloadOnClick={() => {
        handleDownloadOnClick();
      }}
      title={
        <SortBySelect
          defaultValue={localByType.value}
          onChange={(_key: string, value: any) => {
            buriedBtnPoint('KMCM_ByMetricsType', {
              tab,
              keyMetric: keyMetric.value,
              byType: value,
            });
            setByType?.({ value });
            setLocalByType({ value });
          }}
          defaultParams={{
            options,
            btnTextPrefix: formatMessage({ id: title || NO_DATE.EMPTY }),
            matchButtonWidth: true,
          }}
        />
      }
      loading={loading}
      loadingHeight={338}
      empty={data.empty}
      logBtnId="KMCM"
      extraInfo={{
        tab,
        keyMetric: keyMetric.value,
        byType: localByType.value,
      }}
      downloadLoading={downloadLoading || downloading}
      className="dbo-community-left-chart"
    >
      <DetailWrapper
        eventProps={eventProps}
        detailPage={enableDetailPage}
        isDetail={isDetail}
        updateDetailFilter={updateDetailFilter}
        formatParamsKey={formatParamsKey}
        jumpType={JumpType.Window}
      >
        {({ eventsDict, tableWrapper, onChartReady }) => (
          <>
            {chartType === 'table' ? (
              <Table
                data={data?.tableData}
                byType={localByType.value}
                columns={data?.tableColumns}
                formatTimeFunc={formatTimeFunc}
                formatValue={formatValue}
                title={title}
                eventsDict={eventsDict}
                tableWrapper={tableWrapper}
                detailPage={enableDetailPage}
              />
            ) : (
              <Chart
                tab={tab}
                keyMetric={keyMetric.value}
                byType={localByType.value}
                chartType={chartType}
                filter={params}
                data={data}
                title={title}
                isPercent={keyMetric.value === 'engagement_rate'}
                formatValue={formatValue}
                eventsDict={eventsDict}
                onChartReady={onChartReady}
                detailPage={enableDetailPage}
                isDetail={isDetail}
                gameInfo={gameInfo}
                eventProps={eventProps}
                formatParamsKey={formatParamsKey}
                pageFilter={filter}
              />
            )}
          </>
        )}
      </DetailWrapper>
    </CardCommon>
  );
}
